import { Alert, Box, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { TotpMultiFactorGenerator, multiFactor } from "firebase/auth";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton.tsx";
import ConfirmationPopup from "../../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import { firebaseAuth } from "../../../../../config/firebase.ts";
import { POSTHOG_EVENTS } from "../../../../../constants/PostHogEvents";
import RouterConstants from "../../../../../constants/RouterConstants.ts";
import usePostHog from "../../../../../hooks/usePostHog.tsx";
import useSecureNavigate from "../../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import styles from "./MfaTotpSetupPage.module.scss";

const MfaTotpSetupPage = () => {
	const { t } = useTranslation("translation", { keyPrefix: "mfa.totpSetupPage" });
	const { trackEvent } = usePostHog();
	const [step, setStep] = useState<"showQr" | "verify" | "done">("showQr");
	const [error, setError] = useState<string>("");
	const [verificationCode, setVerificationCode] = useState<string>("");
	const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
	const [secret, setSecret] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const currentUser = firebaseAuth.currentUser;
	const [openLoginRequiredPopup, setOpenLoginRequiredPopup] = useState(false);
	const { authStore } = useAppStore();

	const navigate = useSecureNavigate();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const continueUrl = queryParams.get("continueUrl") || undefined;
	const state = location.state;

	// Protection: Redirect if MFA is already completed
	if (authStore.isMfaSetupCompleted) {
		return <Navigate to="/" replace />;
	}

	const handleGenerateQr = async () => {
		if (!currentUser) return;
		setLoading(true);
		setError("");

		// Track MFA setup started
		trackEvent(POSTHOG_EVENTS.MFA_SETUP_STARTED, {
			email: currentUser.email,
		});

		try {
			const session = await multiFactor(currentUser).getSession();
			const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
			setSecret(totpSecret);
			const uri = totpSecret.generateQrCodeUrl(currentUser.email!, "Bria");
			setQrCodeUrl(uri);
			setStep("verify");
		} catch (e: any) {
			trackEvent(POSTHOG_EVENTS.MFA_SETUP_FAILED, {
				email: currentUser.email,
				error:
					e.code === "auth/requires-recent-login"
						? t("error.requiresRecentLogin")
						: e.code === "auth/maximum-second-factor-count-exceeded"
						? t("error.maxMfaError")
						: t("error.genericError"),
			});

			if (e.code === "auth/requires-recent-login") {
				setOpenLoginRequiredPopup(true);
			} else if (e.code === "auth/maximum-second-factor-count-exceeded") {
				setError(t("error.maxMfaError"));
			} else {
				setError(t("error.genericError"));
			}
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyCode = async () => {
		if (!currentUser || !secret) return;
		setLoading(true);
		setError("");
		try {
			const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, verificationCode);
			await multiFactor(currentUser).enroll(assertion, "Bria");
			authStore.isMfaSetupCompleted = true;
			authStore.isMfaSetupRequired = true;

			// Track successful MFA setup completion
			trackEvent(POSTHOG_EVENTS.MFA_SETUP_COMPLETED, {
				email: currentUser.email,
			});

			setStep("done");
		} catch (e: any) {
			if (e.code === "auth/invalid-verification-code") {
				setError(t("error.invalidCodeError"));
			} else {
				setError(t("error.genericError"));
			}
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		await authStore.logout();
		window.location.reload();
	};

	const handleSetupFinish = async () => {
		if (continueUrl) {
			const formattedUrl = continueUrl.startsWith("/") ? continueUrl : `/${continueUrl}`;
			navigate(formattedUrl, { state: state });
		} else {
			navigate(RouterConstants.CONSOLE.fullPath, { replace: true });
		}
	};

	return (
		<Box className={styles.tab}>
			<Box className={styles.container}>
				<Typography className={styles.title}>{t("title")}</Typography>

				{step === "showQr" && (
					<>
						<Box className={styles.descriptionContainer}>
							<Typography
								className={styles.description}
								dangerouslySetInnerHTML={{ __html: t("showQrSection.description") }}
							></Typography>
							<Box className={styles.stepsContainer}>
								<Typography component="span" className={styles.description}>
									{t("showQrSection.steps.step1")}
								</Typography>
								<Typography component="span" className={styles.details}>
									{t("showQrSection.steps.step1Details")}
								</Typography>
								<Typography component="span" className={styles.description}>
									{t("showQrSection.steps.step2")}
								</Typography>
								<Typography component="span" className={styles.description}>
									{t("showQrSection.steps.step3")}
								</Typography>
							</Box>
						</Box>
						<BriaButton
							className={styles.button}
							buttonType="primaryMedium"
							loading={loading}
							disabled={loading}
							onClick={handleGenerateQr}
						>
							{t("showQrSection.generateQrButton")}
						</BriaButton>
						<Typography className={styles.or}>{t("showQrSection.or")}</Typography>
						<BriaButton
							className={clsx(styles.button, styles.switchButton)}
							buttonType="primaryMedium"
							loading={loading}
							disabled={loading}
							onClick={handleLogout}
						>
							{t("showQrSection.switchButton")}
						</BriaButton>
					</>
				)}

				{step === "verify" && (
					<>
						<Typography className={styles.subtitle}>{t("verifySection.title")}</Typography>
						{qrCodeUrl && (
							<Box
								component="img"
								className={styles.qrCodeImage}
								src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
									qrCodeUrl,
								)}&size=200x200`}
							/>
						)}
						<Typography className={styles.subtitle}>{t("verifySection.authenticatorText")}</Typography>
						<TextField
							className={styles.inputField}
							label={t("verifySection.codeInputLabel")}
							value={verificationCode}
							onChange={(e) => setVerificationCode(e.target.value)}
						/>
						<BriaButton
							className={styles.button}
							buttonType="primaryMedium"
							loading={loading}
							disabled={loading}
							onClick={handleVerifyCode}
						>
							{t("verifySection.verifyButton")}
						</BriaButton>
					</>
				)}

				{step === "done" && (
					<Box>
						<Typography className={styles.successMessage}>{t("doneSection.title")}</Typography>
						<BriaButton
							className={clsx(styles.button, styles.switchButton)}
							buttonType="primaryMedium"
							onClick={handleSetupFinish}
						>
							{t("doneSection.finishButton")}
						</BriaButton>
					</Box>
				)}

				{error && (
					<Alert className={styles.alert} severity="error">
						{error}
					</Alert>
				)}

				{/*Popup for requiring login due to recent login error from firebase*/}
				<ConfirmationPopup
					onClose={() => {
						setOpenLoginRequiredPopup(false);
					}}
					title={t("popupSection.title")}
					description={t("popupSection.description")}
					confirmButtonText={t("popupSection.confirmButton")}
					open={openLoginRequiredPopup}
					onClick={authStore.logout}
					hideCancel={false}
					loading={loading}
					disabled={loading}
				/>
			</Box>
		</Box>
	);
};

export default observer(MfaTotpSetupPage);
